import { useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, User as UserIcon, Settings as SettingsIcon, HelpCircle, Bell } from "lucide-react";
import { useAnnouncementCenter } from "@/features/product-announcements/useAnnouncementCenter";
import { AnnouncementCenterModal } from "@/features/product-announcements/AnnouncementCenterModal";
import { NotificationBadge } from "@/features/product-announcements/NotificationBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/components/AuthProvider";
import { useCabinets } from "@/hooks/cabinet/useCabinetCore";
import { useCabinetMemberMe } from "@/hooks/useCabinetMemberMe";

interface AvatarMenuProps {
  onSignOut: () => void;
  /** Mode rail replié : n'affiche que l'avatar (centré), sans nom de cabinet ni rôle. */
  collapsed?: boolean;
}

/**
 * Avatar dropdown anchored at the bottom of AppSidebar (V1.2).
 * Replaces the standalone Logout button with a richer menu :
 * workspace card on top, account section, help, and Logout.
 */
export function AvatarMenu({ onSignOut, collapsed = false }: AvatarMenuProps) {
  const { user } = useAuth();
  const { data: cabinets } = useCabinets();
  const { data: memberMe } = useCabinetMemberMe();

  const cabinet = cabinets?.[0];
  // Nom enregistré (`users.full_name`, via /me) d'abord : la copie du jeton Supabase
  // peut avoir manqué la dernière modification faite dans Paramètres → Profil.
  const fullName =
    memberMe?.full_name ||
    (user?.user_metadata?.full_name as string | undefined) ||
    user?.email ||
    "Utilisateur";

  // First letter of the first name only — reads cleaner in the small 24px avatar.
  const initial = (fullName.trim().charAt(0) || "?").toUpperCase();

  const avatarUrl = user?.user_metadata?.avatar_url as string | undefined;

  const roleLabel =
    memberMe?.cabinet_role === "admin"
      ? "Administrateur"
      : memberMe?.cabinet_role === "member"
      ? "Membre"
      : null;

  const center = useAnnouncementCenter();
  const [notifOpen, setNotifOpen] = useState(false);
  const unread = center?.unreadCount ?? 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          // Hauteur FIXE (h-[52px]) dans les DEUX états : sans ça, le bloc texte (nom de
          // cabinet + rôle, 2 lignes) rend le trigger plus haut en déplié → en repliant
          // (texte masqué) il rétrécit et, le groupe du bas étant ancré en bas, l'avatar
          // ET l'Aide remontaient. À hauteur fixe + boîte d'icône (w-10) en tête, l'avatar
          // (centré à 28px) garde EXACTEMENT sa position dans les deux états. Texte (nom
          // de cabinet + rôle) masqué en replié.
          className="relative flex h-[52px] w-full items-center rounded-lg hover:bg-[#F0F0EE] focus:outline-none focus-visible:ring-2 focus-visible:ring-grey-400/50"
          aria-label={
            unread > 0
              ? `Ouvrir le menu utilisateur, ${unread} ${unread > 1 ? 'notifications non lues' : 'notification non lue'}`
              : "Ouvrir le menu utilisateur"
          }
        >
          {/* Boîte d'icône largeur fixe (w-10) → avatar centré à 28px (= centre du rail),
              identique en replié/déplié (pas de glissement horizontal). */}
          <span className="flex w-10 flex-shrink-0 items-center justify-center">
            <div className="relative">
              <Avatar className="h-6 w-6 border border-black/5">
                {avatarUrl ? <AvatarImage src={avatarUrl} alt={fullName} /> : null}
                <AvatarFallback className="bg-[#2E2F33] text-white text-xs font-medium">
                  {initial}
                </AvatarFallback>
              </Avatar>
              <NotificationBadge count={unread} className="absolute -top-1 -right-1 ring-2 ring-grey-100" />
            </div>
          </span>
          {/* Nom du cabinet + rôle à côté de l'avatar — masqués en mode rail. */}
          {!collapsed && (
            <div className="flex flex-col items-start min-w-0 text-left">
              <span className="text-[13px] font-medium text-charcoal truncate max-w-[160px]">
                {cabinet?.name || "Cabinet"}
              </span>
              {roleLabel ? (
                <span className="text-xs text-grey-600 truncate max-w-[160px]">{roleLabel}</span>
              ) : null}
            </div>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="end" className="w-64">
          <DropdownMenuItem asChild>
            <Link to="/settings#profile">
              <UserIcon className="mr-2 h-4 w-4" />
              Mon profil
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings">
              <SettingsIcon className="mr-2 h-4 w-4" />
              Paramètres
            </Link>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {center && center.items.length > 0 && (
            <DropdownMenuItem
              onSelect={() => setNotifOpen(true)}
              aria-label={
                unread > 0
                  ? `Notifications, ${unread} non ${unread > 1 ? 'lues' : 'lue'}`
                  : 'Notifications'
              }
            >
              <Bell className="mr-2 h-4 w-4" />
              <span className="flex-1">Notifications</span>
              <NotificationBadge count={unread} />
            </DropdownMenuItem>
          )}

          <DropdownMenuItem asChild>
            <Link to="/aide">
              <HelpCircle className="mr-2 h-4 w-4" />
              Aide
            </Link>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="text-red-600 focus:text-red-700 focus:bg-red-50"
            onSelect={() => onSignOut()}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Déconnexion
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* AnnouncementCenterModal is rendered OUTSIDE DropdownMenuContent (sibling of DropdownMenu).
          If it were inside, Radix would unmount it on menu close, creating a focus race. */}
      {center && center.items.length > 0 && (
        <AnnouncementCenterModal
          announcements={center.items}
          readIds={center.readIds}
          open={notifOpen}
          onClose={() => setNotifOpen(false)}
          onMarkRead={center.markRead}
          onMarkAllRead={center.markAllRead}
        />
      )}
    </>
  );
}

export default AvatarMenu;
